import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ItemReorderEventDetail } from '@ionic/angular/standalone';
import { IonReorderGroup, IonIcon, IonCheckbox, IonReorder, IonList, IonItem, IonLabel, IonInput, IonTextarea, IonToggle } from '@ionic/angular/standalone';
import { AgreementBullet, CollectionTemplateAgreement } from '../../models/CollectionTemplateAgreement';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SexyModule } from '@qid/sexy';
import { SuperButtonComponent } from '@qid/super';

@Component({
  selector: 'app-template-agreement-setter',
  templateUrl: './template-agreement-setter.component.html',
  styleUrls: ['./template-agreement-setter.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SexyModule,
    SuperButtonComponent,
    IonReorderGroup,
    IonIcon,
    IonCheckbox,
    IonReorder,
    IonList,
    IonItem,
    IonLabel,
    IonInput,
    IonTextarea,
    IonToggle
  ],
})
export class TemplateAgreementSetterComponent implements OnInit {
  @Input() collectionTemplateAgreements: CollectionTemplateAgreement[] = [];
  @Output() collectionTemplateAgreementsChange = new EventEmitter<CollectionTemplateAgreement[]>();
  selectedAgreementIndex: number = null;

  public baseIconUrl = 'assets/'
  public allIcons: { imgUrl: string, isSelected?: boolean }[] = [
    { imgUrl: 'no-drink.png' },
    { imgUrl: 'no-smoking.png' },
    { imgUrl: 'no-drugs.png' },
    { imgUrl: 'no-camera.png' },
    { imgUrl: 'no-pets.png' }
  ]

  public validationErrors: { [key: number]: string[] } = {};
  public showErrors: boolean = false;
  public fieldErrors: { 
    title: { [key: number]: boolean },
    bullets: { [key: number]: { [bulletIndex: number]: boolean } }
  } = { title: {}, bullets: {} };

  constructor() {
  }

  ngOnInit() {
  }

  addPolicy() {
    this.collectionTemplateAgreements.push(new CollectionTemplateAgreement());
    this.selectedAgreementIndex = this.collectionTemplateAgreements.length - 1;
  }

  selectAgreement(agreement) {
    this.selectedAgreementIndex = agreement;
    this.setDefaultIcons();
  }

  handleReorder(ev: CustomEvent<ItemReorderEventDetail>) {
    this.collectionTemplateAgreements = ev.detail.complete(
      this.collectionTemplateAgreements
    );
    this.collectionTemplateAgreementsChange.emit(
      this.collectionTemplateAgreements
    );
  }

  remove(i: number) {
    this.collectionTemplateAgreements.splice(i, 1)
    // Remove validation errors for this index
    delete this.validationErrors[i];
    // Reindex validation errors
    const newErrors: { [key: number]: string[] } = {};
    Object.keys(this.validationErrors).forEach(key => {
      const numKey = parseInt(key);
      if (numKey > i) {
        newErrors[numKey - 1] = this.validationErrors[numKey];
      } else if (numKey < i) {
        newErrors[numKey] = this.validationErrors[numKey];
      }
    });
    this.validationErrors = newErrors;
    this.selectAgreement(null)
  }

  addBullet() {
    this.collectionTemplateAgreements[this.selectedAgreementIndex].bullets.push(new AgreementBullet())
  }

  addIcon(imgName: string) {
    this.collectionTemplateAgreements[this.selectedAgreementIndex].icons.push({ imgName: imgName })
  }

  removeBullet(index: number) {
    this.collectionTemplateAgreements[this.selectedAgreementIndex].bullets.splice(index, 1)
  }

  removeIcon(index: number) {
    this.collectionTemplateAgreements[this.selectedAgreementIndex].icons.splice(index, 1)
  }

  selectIcon(i: number) {
    this.allIcons[i].isSelected = !this.allIcons[i].isSelected;

    const imgNames: string[] = this.collectionTemplateAgreements[this.selectedAgreementIndex].icons?.map(icon => icon.imgName);

    if (this.allIcons[i].isSelected && imgNames.indexOf(this.allIcons[i].imgUrl) < 1) {
      this.addIcon(this.allIcons[i].imgUrl)
    }

    if (!this.allIcons[i].isSelected && imgNames.indexOf(this.allIcons[i].imgUrl) > -1) {
      this.removeIcon(imgNames.indexOf(this.allIcons[i].imgUrl))
    }

  }

  setDefaultIcons() {
    const imgNames: string[] = this.collectionTemplateAgreements[this.selectedAgreementIndex].icons?.map(icon => icon.imgName);
    for (let icon of this.allIcons) {
      icon.isSelected = imgNames.indexOf(icon.imgUrl) > -1
    }
  }

  /**
   * Validates if an agreement has required fields filled
   * @param index - Index of the agreement to validate
   * @returns true if valid, false otherwise
   */
  private validateAgreement(index: number): boolean {
    const agreement = this.collectionTemplateAgreements[index];
    const errors: string[] = [];

    if (!agreement) {
      return true;
    }

    // Initialize field errors for this agreement
    if (!this.fieldErrors.bullets[index]) {
      this.fieldErrors.bullets[index] = {};
    }

    // Check if title is empty or only whitespace
    if (!agreement.title || agreement.title.trim() === '') {
      errors.push('Policy title is required');
      this.fieldErrors.title[index] = true;
    } else {
      this.fieldErrors.title[index] = false;
    }

    // Check if there are bullets with empty content
    if (agreement.bullets && agreement.bullets.length > 0) {
      agreement.bullets.forEach((bullet, bulletIndex) => {
        const hasTitle = bullet.title && bullet.title.trim() !== '';
        const hasText = bullet.text && bullet.text.trim() !== '';
        
        if (!hasTitle && !hasText) {
          errors.push(`Bullet ${bulletIndex + 1} is empty. Please add content or remove it.`);
          this.fieldErrors.bullets[index][bulletIndex] = true;
        } else {
          this.fieldErrors.bullets[index][bulletIndex] = false;
        }
      });
    }

    if (errors.length > 0) {
      this.validationErrors[index] = errors;
      return false;
    } else {
      delete this.validationErrors[index];
      return true;
    }
  }

  /**
   * Validates all agreements before saving
   * @returns true if all valid, false otherwise
   */
  public validateAllAgreements(): boolean {
    this.validationErrors = {};
    this.fieldErrors = { title: {}, bullets: {} };
    this.showErrors = false;
    
    let allValid = true;
    for (let i = 0; i < this.collectionTemplateAgreements.length; i++) {
      if (!this.validateAgreement(i)) {
        allValid = false;
      }
    }
    
    if (!allValid) {
      this.showErrors = true;
    }
    
    return allValid;
  }

  /**
   * Get validation errors for a specific agreement
   */
  public getAgreementErrors(index: number): string[] {
    return this.validationErrors[index] || [];
  }

  /**
   * Check if an agreement has errors
   */
  public hasErrors(index: number): boolean {
    return this.showErrors && this.validationErrors[index] && this.validationErrors[index].length > 0;
  }

  /**
   * Check if title field has error for specific agreement
   */
  public hasTitleError(index: number): boolean {
    return this.showErrors && this.fieldErrors.title[index] === true;
  }

  /**
   * Check if bullet field has error
   */
  public hasBulletError(agreementIndex: number, bulletIndex: number): boolean {
    return this.showErrors && 
           this.fieldErrors.bullets[agreementIndex] && 
           this.fieldErrors.bullets[agreementIndex][bulletIndex] === true;
  }

  /**
   * Check if current selected agreement's title has error
   */
  public hasSelectedTitleError(): boolean {
    return this.selectedAgreementIndex !== null && this.hasTitleError(this.selectedAgreementIndex);
  }

  /**
   * Check if current selected agreement's bullet has error
   */
  public hasSelectedBulletError(bulletIndex: number): boolean {
    return this.selectedAgreementIndex !== null && this.hasBulletError(this.selectedAgreementIndex, bulletIndex);
  }

  /**
   * Clear title error when user starts typing
   */
  public onTitleChange(): void {
    if (this.selectedAgreementIndex !== null && this.showErrors) {
      const agreement = this.collectionTemplateAgreements[this.selectedAgreementIndex];
      if (agreement.title && agreement.title.trim() !== '') {
        this.fieldErrors.title[this.selectedAgreementIndex] = false;
        this.updateValidationErrors(this.selectedAgreementIndex);
      }
    }
  }

  /**
   * Clear bullet error when user starts typing
   */
  public onBulletChange(bulletIndex: number): void {
    if (this.selectedAgreementIndex !== null && this.showErrors) {
      const bullet = this.collectionTemplateAgreements[this.selectedAgreementIndex].bullets[bulletIndex];
      const hasTitle = bullet.title && bullet.title.trim() !== '';
      const hasText = bullet.text && bullet.text.trim() !== '';
      
      if (hasTitle || hasText) {
        if (this.fieldErrors.bullets[this.selectedAgreementIndex]) {
          this.fieldErrors.bullets[this.selectedAgreementIndex][bulletIndex] = false;
        }
        this.updateValidationErrors(this.selectedAgreementIndex);
      }
    }
  }

  /**
   * Update validation errors for an agreement after field changes
   */
  private updateValidationErrors(index: number): void {
    const hasAnyError = this.fieldErrors.title[index] || 
                        (this.fieldErrors.bullets[index] && 
                         Object.values(this.fieldErrors.bullets[index]).some(error => error === true));
    
    if (!hasAnyError) {
      delete this.validationErrors[index];
    }
  }

}
